import React, { useEffect, useState, useRef } from "react";
import Footer from "../component/Footer";
import Header from "../component/Header";
import ScrollToTop from "../component/ScrollToTop";
import ContactModal from "../component/ContactModal";

export default function Home() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  useEffect(() => {
    const elements = document.querySelectorAll(".process_item");

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveStep(Number(entry.target.dataset.index));
          }
        });
      },
      {
        threshold: 0.5,
      }
    );

    elements.forEach((element) => observer.observe(element));

    return () => observer.disconnect();
  }, []);

  const steps = [
    {
      number: "01",
      title: "Discovery",
      description:
        "Understanding business goals, user requirements, workflows and technical needs.",
    },
    {
      number: "02",
      title: "Architecture",
      description:
        "Planning technology stack, database structure, APIs and infrastructure.",
    },
    {
      number: "03",
      title: "Development",
      description:
        "Building frontend, backend, integrations and complete software systems.",
    },
    {
      number: "04",
      title: "Testing",
      description:
        "Ensuring quality, security and performance through detailed testing.",
    },
    {
      number: "05",
      title: "Deployment",
      description:
        "Launching reliable production-ready solutions.",
    },
    {
      number: "06",
      title: "Growth & Scale",
      description:
        "Continuous improvements, optimization and feature expansion.",
    },
  ];

  // ==== serve-sec========
  const sectionRef = useRef(null);

  useEffect(() => {
    const cards = sectionRef.current?.querySelectorAll(".service-card");

    if (!cards) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("service-card-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.15,
      }
    );

    cards.forEach((card) => observer.observe(card));

    return () => observer.disconnect();
  }, []);

  const services = [
    {
      id: "01",
      title: "Custom Software Development",
      description:
        "Build business-specific software solutions designed around your workflows, users and operational requirements.",
      icon: "⌘",
      tags: [
        "Enterprise Software",
        "Business Applications",
        "Management Systems",
        "Automation Platforms",
      ],
    },
    {
      id: "02",
      title: "Artificial Intelligence Solutions",
      description:
        "Develop intelligent AI-powered systems that automate processes and improve business efficiency.",
      icon: "✦",
      tags: [
        "AI Assistants",
        "Chatbots",
        "Machine Learning",
        "AI Automation",
      ],
    },
    {
      id: "03",
      title: "Blockchain Development",
      description:
        "Create secure blockchain applications and decentralized solutions for future-ready businesses.",
      icon: "✣",
      tags: [
        "Smart Contracts",
        "Web3 Apps",
        "Blockchain Platforms",
        "Wallet Integration",
      ],
    },
    {
      id: "04",
      title: "SaaS Product Development",
      description:
        "Build scalable cloud-based software products designed for subscription-based businesses.",
      icon: "◈",
      tags: [
        "SaaS Platforms",
        "Customer Dashboards",
        "Admin Panels",
        "Cloud Applications",
      ],
    },
    {
      id: "05",
      title: "Mobile Application Development",
      description:
        "Design and develop high-performance mobile applications for businesses and customers.",
      icon: "⌁",
      tags: [
        "Android Apps",
        "iOS Apps",
        "Business Apps",
        "Customer Apps",
      ],
    },
    {
      id: "06",
      title: "API & Integration Solutions",
      description:
        "Connect business systems through secure APIs and automated workflows.",
      icon: "◌",
      tags: [
        "API Development",
        "Third-party Integration",
        "System Connections",
        "Workflow Automation",
      ],
    },
  ];

  // technology sec=========
  const technologies = [
    {
      id: "01",
      name: "Artificial Intelligence",
      short: "AI",
      description:
        "Build intelligent systems that automate complex processes, understand data and create smarter digital experiences.",
      points: [
        "Machine Learning",
        "AI Assistants",
        "Predictive Systems",
        "Intelligent Automation",
      ],
      color: "#635BFF",
    },
    {
      id: "02",
      name: "Blockchain",
      short: "WEB3",
      description:
        "Create secure decentralized applications and trusted digital ecosystems powered by blockchain technology.",
      points: [
        "Smart Contracts",
        "Web3 Applications",
        "Digital Assets",
        "Decentralized Systems",
      ],
      color: "#F59E0B",
    },
    {
      id: "03",
      name: "Cloud Computing",
      short: "CLOUD",
      description:
        "Build scalable cloud infrastructure that keeps modern applications fast, reliable and ready to grow.",
      points: [
        "Cloud Architecture",
        "Secure Hosting",
        "Data Infrastructure",
        "Scalable Systems",
      ],
      color: "#06B6D4",
    },
    {
      id: "04",
      name: "Software Architecture",
      short: "ARCH",
      description:
        "Design robust software architectures that remain flexible, maintainable and scalable as your business evolves.",
      points: [
        "APIs",
        "Backend Systems",
        "Database Systems",
        "Enterprise Architecture",
      ],
      color: "#8B5CF6",
    },
    {
      id: "05",
      name: "Cyber Security",
      short: "SECURITY",
      description:
        "Protect applications, infrastructure and critical business data with modern security-first engineering practices.",
      points: [
        "Application Security",
        "Data Protection",
        "Identity Management",
        "Threat Detection",
      ],
      color: "#10B981",
    },
    {
      id: "06",
      name: "Mobile Technology",
      short: "MOBILE",
      description:
        "Create seamless mobile experiences with high-performance native and cross-platform application development.",
      points: [
        "iOS Applications",
        "Android Applications",
        "Cross Platform",
        "Mobile UX",
      ],
      color: "#EC4899",
    },
  ];

  const [activeTech, setActiveTech] = useState(0);

  const currentTech = technologies[activeTech];
  return (
    <>
      <ScrollToTop>
        <Header />
        <section className="hero_img position-relative hero_padding">
          <div className="container">
            <div className="row my-5">
              <div className="col-lg-7">
                <h6 className="sub_para small_text text-uppercase mb-xl-4 gradient-text
" >
                  Software · AI · Blockchain · Cloud
                </h6>
                <h1 className="heading-1 mb-xl-4 mb-3">
                  Build Intelligent Software. <br className="d-md-block d-none" /> Scale Digital Innovation.
                </h1>
                <p className="sub_para body-regular mb-4">
                  GenX Meta creates next-generation software products, AI-powered solutions, blockchain ecosystems and scalable digital platforms that help businesses transform ideas into powerful technology solutions.
                </p>
                <div className="d-flex gap-3">
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(true)}
                    className="btn genxmeta_button hero_btn font_14 fw-bold"
                  >
                    Start Your Project
                  </button>

                  <button
                    type="button"
                    onClick={() => setIsModalOpen(true)}
                    className="btn genxmeta_button gray_btn hero_btn font_14 fw-bold"
                  >
                    Explore Services
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="section_padding features_section_premium">
          <div className="container">
            <div className="max-800">
              <h6 className="sub_para small_text text-uppercase mb-xl-4 gradient-text
" >
                introduction
              </h6>
              <h2 className="heading-2 mb-2 text-white">
                We Build Technology That Moves Businesses Forward
              </h2>
              <p className="sub_para body-regular mb-5">
                GenX Meta is a software development and technology company focused on building scalable digital products for modern businesses. We combine software engineering, artificial intelligence, blockchain technology and cloud solutions to create secure, reliable and growth-focused technology platforms.
              </p>
            </div>
            <div className="row text-white g-md-5 g-3">
              <div className="col-lg-4 col-md-6">
                <h6 className="sub_heading">Build</h6>
                <p className="sub_para para_color">
                  Create powerful software products with modern architecture, secure systems and scalable technology.
                </p>
              </div>
              <div className="col-lg-4 col-md-6">
                <h6 className="sub_heading">Launch</h6>
                <p className="sub_para para_color">
                  Transform innovative ideas into market-ready digital solutions.
                </p>
              </div>
              <div className="col-lg-4 col-md-6">
                <h6 className="sub_heading">Scale</h6>
                <p className="sub_para para_color">
                  Improve performance, adoption and business growth through technology.
                </p>
              </div>
              {/* <div className="col-lg-4 col-md-6">
                <h6 className="sub_heading">Mobile Apps</h6>
                <p className="sub_para para_color">
                  Build iOS, Android and Cross Platform applications with App
                  store listing compliance.
                </p>
              </div>
              <div className="col-lg-4 col-md-6">
                <h6 className="sub_heading">Cyber Security</h6>
                <p className="sub_para para_color">
                  Enhance cybersecurity posture, increase compliance and build
                  digital resilience.
                </p>
              </div>
              <div className="col-lg-4 col-md-6">
                <h6 className="sub_heading">UI/UX Design</h6>
                <p className="sub_para para_color">
                  Create Design Systems, Interface Designs and User Experiences
                  for your clients.
                </p>
              </div> */}
            </div>
          </div>
        </section>

        <section className="overflow-hidden engineers_section_premium">
          <div className="row align-items-center g-xl-5 g-0">
            <div className="col-xl-6 p-4">
              <div className="h-100">
                <img
                  src="assets/genxmeta_engineers_banner.jpg"
                  alt=""
                  className="w-100 eye_img"
                />
              </div>
            </div>
            <div className="col-xl-6 ">
              <div className="left_padding">
                <div className="row">
                  <div className="col-xl-8">
                    <h2 className="heading-2 mb-xl-4 mb-3">
                      For Engineers, by Engineers.
                    </h2>
                    <p className="sub_para">
                      With international recognition of our work, our software
                      engineering expertise is deep rooted in our DNA - creating
                      functional products and writing compelling code.
                    </p>
                    <div className="row mt-4 g-3">
                      <div className="col-6">
                        <p className="font_14 mb-md-2 mb-1">
                          Direct consumer impact
                        </p>
                        <p className="sub_heading">15 million</p>
                      </div>
                      <div className="col-6">
                        <p className="font_14 mb-md-2 mb-1">
                          Projects launched
                        </p>
                        <p className="sub_heading">50+</p>
                      </div>
                      <div className="col-6">
                        <p className="font_14 mb-md-2 mb-1">
                          Engineering Experience
                        </p>
                        <p className="sub_heading">35 Years</p>
                      </div>
                      <div className="col-6">
                        <p className="font_14 mb-md-2 mb-1">Talent</p>
                        <p className="sub_heading">15 Specialists</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        <section className="section_padding clients_section_premium">
          <div className="container">
            <div className="row g-4">
              <h2 className="heading-2 mb-xl-5 mb-lg-4 mb-2 text-center text-sm-start">
                Building better. Together.
              </h2>
              <div className="col-lg-2 col-md-4 col-6 ">
                <div className="logo_card">
                  <div className="logo_card_inner">
                    <img
                      src="assets/img/639b830a4cbdd20fd5f3a9a9/63a399bf04383df7ba914fb3_algorand-logo%403x.png"
                      alt=""
                      className="img-fluid"
                    />
                  </div>
                </div>
              </div>
              <div className="col-lg-2 col-md-4 col-6">
                <div className="logo_card">
                  <div className="logo_card_inner">
                    <img
                      src="assets/img/639b830a4cbdd20fd5f3a9a9/63a399c0c8c1bd23a3dfb01e_google-logo%403x.png"
                      alt=""
                      className="img-fluid"
                    />
                  </div>
                </div>
              </div>
              <div className="col-lg-2 col-md-4 col-6 ">
                <div className="logo_card">
                  <div className="logo_card_inner">
                    <img
                      src="assets/img/639b830a4cbdd20fd5f3a9a9/63a399bfefca1136c6c37597_aws-logo%403x.png"
                      alt=""
                      className="img-fluid"
                    />
                  </div>
                </div>
              </div>
              <div className="col-lg-2 col-md-4 col-6">
                <div className="logo_card">
                  <div className="logo_card_inner">
                    <img
                      src="assets/img/639b830a4cbdd20fd5f3a9a9/63a399bf2393cc0df3939a40_certik-logo%403x.png"
                      alt=""
                      className="img-fluid"
                    />
                  </div>
                </div>
              </div>
              <div className="col-lg-2 col-md-4 col-6">
                <div className="logo_card">
                  <div className="logo_card_inner">
                    <img
                      src="assets/img/639b830a4cbdd20fd5f3a9a9/63a399c0a9186364e8d218dc_immuniweb-logo%403x.png"
                      alt=""
                      className="img-fluid"
                    />
                  </div>
                </div>
              </div>
              <div className="col-lg-2 col-md-4 col-6">
                <div className="logo_card">
                  <div className="logo_card_inner">
                    <img
                      src="assets/img/639b830a4cbdd20fd5f3a9a9/63a399c1c031257c45a039b4_meta-logo%402x.png"
                      alt=""
                      className="img-fluid"
                    />
                  </div>
                </div>
              </div>
              <p className="sub_para mt-lg-5 mt-3 text-center text-sm-start">
                We team up with world's leading solution providers who are
                obsessed with building highest-quality products and experiences.
              </p>
            </div>
          </div>
        </section>
        <section className="section_padding qoute-block">
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-lg-6">
                <h2 className="sub_title text-white mb-xl-4 mb-3">
                  Ready To Build Your Next Digital Product?
                </h2>
                <p className="sub_para body-regular">
                  Partner with GenX Meta to transform your ideas into scalable technology solutions.
                </p>
                <button
                  type="button"
                  onClick={() => setIsModalOpen(true)}
                  className="btn genxmeta_button hero_btn font_14 fw-bold mt-xl-3 mt-2"
                >
                  Start Discovery
                </button>
              </div>
            </div>
          </div>
        </section>

        <section className="services_sec section_padding">
          <div className="container">
            <div className="max-800">
              <h6 className="sub_para small_text text-uppercase mb-xl-4 gradient-text
" >
                Why GenX Meta
              </h6>
              <h2 className="heading-2 mb-2 text-white">
                Why Choose GenX Meta?
              </h2>
              <p className="sub_para body-regular mb-5">
                We don't just develop software. We create complete digital ecosystems that help businesses build, launch and grow.
              </p>
            </div>

            <div className="row g-4">
              <div className="col-md-6 col-12 d-flex">
                <div className="services_card h-100 w-100">
                  <div className="d-flex flex-column gap-3">
                    <div className="feature-box">
                      <svg viewBox="0 0 24 24" fill="none" stroke="#0072ff" stroke-width="1.8"><path d="M12 2 L2 7 L12 12 L22 7 Z"></path><path d="M2 17 L12 22 L22 17"></path><path d="M2 12 L12 17 L22 12"></path></svg>
                    </div>

                    <h2 className="heading-4 mb-0">Future Ready Technology</h2>
                    <p className="mb-0">Using advanced technologies including AI, cloud computing, blockchain and automation.</p>
                  </div>
                </div>
              </div>

              <div className="col-md-6 col-12 d-flex">
                <div className="services_card h-100 w-100">
                  <div className="d-flex flex-column gap-3">
                    <div className="feature-box">
                      <svg viewBox="0 0 24 24" fill="none" stroke="#6366f1" stroke-width="1.8"><rect x="3" y="3" width="7" height="7" rx="1.5"></rect><rect x="14" y="3" width="7" height="7" rx="1.5"></rect><rect x="3" y="14" width="7" height="7" rx="1.5"></rect><rect x="14" y="14" width="7" height="7" rx="1.5"></rect></svg>
                    </div>

                    <h2 className="heading-4 mb-0">Scalable Architecture</h2>
                    <p className="mb-0">Building software systems designed for performance, security and future expansion.</p>
                  </div>
                </div>
              </div>

              <div className="col-md-6 col-12 d-flex">
                <div className="services_card h-100 w-100">
                  <div className="d-flex flex-column gap-3">
                    <div className="feature-box">
                      <svg viewBox="0 0 24 24" fill="none" stroke="#6366f1" stroke-width="1.8"><circle cx="12" cy="12" r="9"></circle><path d="M8 12h8M12 8v8"></path></svg>
                    </div>

                    <h2 className="heading-4 mb-0">Business Focused Solutions</h2>
                    <p className="mb-0">Every product is developed around business goals, user experience and market requirements.</p>
                  </div>
                </div>
              </div>

              <div className="col-md-6 col-12 d-flex">
                <div className="services_card h-100 w-100">
                  <div className="d-flex flex-column gap-3">
                    <div className="feature-box">
                      <svg viewBox="0 0 24 24" fill="none" stroke="#0072ff" stroke-width="1.8"><path d="M4 19 L10 6 L14 14 L17 9 L21 19 Z"></path></svg>
                    </div>

                    <h2 className="heading-4 mb-0">Complete Digital Journey</h2>
                    <p className="mb-0">From idea discovery to development, deployment and continuous improvement.</p>
                  </div>
                </div>
              </div>

            </div>
          </div>

        </section>

        <section className="process_sec engineers_section_premium">
          <div className="container">
            <div className="row g-3">
              <div className="col-lg-6 col-12">
                <h6 class="sub_para small_text text-uppercase mb-xl-4 gradient-text">Development Process</h6>
                <h2 className="heading-2 mb-xl-4 mb-3">
                  From Idea To Successful Product
                </h2>
                <p className="sub_para">
                  A structured six-stage delivery path, so every build moves from concept to a scaled, production-grade product.
                </p>
              </div>

              <div className="col-lg-6 col-12">
                <div className="process_wrapper">
                  <div className="process_line"></div>

                  {steps.map((step, index) => (
                    <div
                      className={`process_item ${activeStep === index ? "active" : ""
                        }`}
                      data-index={index}
                      key={step.number}
                    >
                      <div className="process_number">
                        {step.number}
                      </div>

                      <div className="process_content">
                        <h3>{step.title}</h3>
                        <p>{step.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

        </section>

        <section className="about_sec services_sec section_padding">
          <div className="container">
            <div className="row g-5">
              <div className="col-lg-6 col-12">
                <div className="">
                  <h6 className="sub_para small_text text-uppercase mb-xl-4 gradient-text
" >
                    About Us
                  </h6>
                  <h2 className="heading-2 mb-2 text-white">
                    Building The Future Through Technology Innovation
                  </h2>
                  <p className="sub_para body-regular">
                    GenX Meta is a technology company focused on developing intelligent software solutions that solve real business challenges.
                  </p>
                  <p className="sub_para body-regular">
                    We help organizations transform ideas into powerful digital products through software engineering, artificial intelligence, blockchain and cloud technologies.
                  </p>
                </div>
              </div>
              <div className="col-lg-6 col-12">
                <div className="row g-2">
                  <div className="col-md-6 col-12 d-flex">
                    <div className="services_card h-100 w-100">
                      <div className="d-flex flex-column gap-3">
                        <h6 className="sub_para mb-0 text-uppercase gradient-text text_14
" >
                          Our Vision
                        </h6>

                        <h2 className="heading-4 mb-0">Our Vision</h2>
                        <p className="mb-0">To empower businesses with innovative technology solutions that create long-term digital growth.</p>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6 col-12 d-flex">
                    <div className="services_card h-100 w-100">
                      <div className="d-flex flex-column gap-3">
                        <h6 className="sub_para mb-0 text-uppercase gradient-text text_14
" >
                          Our Mission
                        </h6>

                        <h2 className="heading-4 mb-0">Our Mission</h2>
                        <p className="mb-0">To design and develop secure, scalable and intelligent digital products that help businesses operate smarter and grow faster.</p>
                      </div>
                    </div>
                  </div>

                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="serv_sec engineers_section_premium" id="services">
          <div className="container">
            <div className="max-800 mb-5">
              <h6 className="sub_para small_text text-uppercase mb-xl-4 gradient-text
" >
                Services
              </h6>
              <h2 className="heading-2 mb-xl-4 mb-3">
                For Engineers, by Engineers.
              </h2>
              <p className="sub_para">
                With international recognition of our work, our software
                engineering expertise is deep rooted in our DNA - creating
                functional products and writing compelling code.
              </p>
            </div>

            <div className="row g-4">
              {services.map((service, index) => (
                <div
                  className="col-lg-4 col-md-6"
                  key={service.id}
                >
                  <div
                    className="service-card"
                    style={{
                      "--delay": `${index * 100}ms`,
                    }}
                  >
                    {/* Animated glow */}
                    <div className="card-glow"></div>

                    {/* Top gradient line */}
                    <div className="card-line"></div>

                    {/* Header */}
                    <div className="service-card-top">
                      <div className="service-icon">
                        <span>{service.icon}</span>
                      </div>

                      <span className="service-number">
                        {service.id}
                      </span>
                    </div>

                    {/* Content */}
                    <div className="service-content">
                      <h3>{service.title}</h3>

                      <p>{service.description}</p>
                    </div>

                    {/* Tags */}
                    <div className="service-tags">
                      {service.tags.map((tag) => (
                        <span key={tag}>{tag}</span>
                      ))}
                    </div>

                    {/* Bottom CTA */}
                    <div className="service-footer">
                      <span>Explore service</span>

                      <div className="service-arrow">
                        →
                      </div>
                    </div>

                    {/* Decorative circle */}
                    <div className="card-orb"></div>
                  </div>
                </div>
              ))}
            </div>

          </div>
        </section>

        <section className="tech_ecosystem_sec">
          <div className="container">

            {/* HEADER */}
            <div className="tech_intro row align-items-end">

              <div className="col-lg-7">
                <h6 className="sub_para small_text text-uppercase mb-xl-4 gradient-text
" >
                  Technology
                </h6>

                <h2 className="heading-2">
                  Powered by
                  <span> modern technologies.</span>
                </h2>
              </div>

              <div className="col-lg-5">
                <p>
                  We combine proven technologies with modern engineering
                  practices to create secure, scalable and intelligent
                  digital solutions.
                </p>
              </div>

            </div>


            {/* MAIN EXPERIENCE */}
            <div className="tech_experience">

              {/* LEFT NAVIGATION */}
              <div className="tech_navigation">

                <div className="tech_nav_line"></div>

                {technologies.map((tech, index) => (
                  <button
                    key={tech.id}
                    className={`tech_nav_item ${activeTech === index ? "active" : ""
                      }`}
                    onMouseEnter={() => setActiveTech(index)}
                    onClick={() => setActiveTech(index)}
                  >

                    <span className="tech_nav_number">
                      {tech.id}
                    </span>

                    <span className="tech_nav_name">
                      {tech.name}
                    </span>

                    <span
                      className="tech_nav_dot"
                      style={{
                        "--tech-color": tech.color,
                      }}
                    ></span>

                  </button>
                ))}

              </div>


              {/* CENTER VISUAL */}
              <div className="tech_visual_area">

                <div
                  className="tech_visual"
                  style={{
                    "--active-color": currentTech.color,
                  }}
                >

                  <div className="tech_visual_ring ring_one"></div>
                  <div className="tech_visual_ring ring_two"></div>
                  <div className="tech_visual_ring ring_three"></div>

                  <div className="tech_visual_core">

                    <span>
                      {currentTech.short}
                    </span>

                  </div>

                  <div className="tech_orbit orbit_one"></div>
                  <div className="tech_orbit orbit_two"></div>

                </div>

              </div>


              {/* CONTENT */}
              <div className="tech_content">

                <div className="tech_content_number">
                  {currentTech.id} / 06
                </div>

                <div className="tech_content_line"></div>

                <h3 key={currentTech.id}>
                  {currentTech.name}
                </h3>

                <p key={`desc-${currentTech.id}`}>
                  {currentTech.description}
                </p>

                <div className="tech_points">

                  {currentTech.points.map((point) => (
                    <span key={point}>
                      <i></i>
                      {point}
                    </span>
                  ))}

                </div>

                <button className="tech_explore_btn">
                  Explore technology
                  <span>↗</span>
                </button>

              </div>

            </div>


            {/* TECHNOLOGY STACK */}
            <div className="tech_stack">

              <span className="stack_label">
                TECHNOLOGY STACK
              </span>

              <div className="stack_items">

                {technologies.map((tech, index) => (
                  <button
                    key={tech.id}
                    onMouseEnter={() => setActiveTech(index)}
                    className={
                      activeTech === index ? "stack_active" : ""
                    }
                  >
                    {tech.name}
                  </button>
                ))}

              </div>

            </div>

          </div>
        </section>

        {/* <section className="liftoffs_light_section">
          <div className="container">
            <div className="row align-items-center gy-xl-5 gy-4">
              <div className="col-md-9">
                <h2 className="heading-2">Recent liftoffs</h2>
                <p className="sub_para mb-0">
                  Browse through our most recent product launches and
                  engineering projects. Our capabilities cover a wide range of
                  production-grade deployments such as Mobile, Web, Serverless
                  and Blockchain applications.
                </p>
              </div>
              <div className="col-md-3 d-md-flex d-none justify-content-end">
                <button type="button" className="btn genxmeta_button hero_btn">
                  View all
                </button>
              </div>
              <div className="col-lg-6">
                <div className="liftoff_card augusta_card">
                  <div>
                    <h3 className="heading-4">
                      Augusta Platforms Inc
                    </h3>
                    <p className="sub_para">
                      Wellness focused social media experience
                    </p>
                    <ul className="list-unstyled boxes">
                      <li className="liftoff_tag">iOS</li>
                      <li className="liftoff_tag">Android</li>
                      <li className="liftoff_tag">Mobile</li>
                      <li className="liftoff_tag">Social Media</li>
                      <li className="liftoff_tag">+3</li>
                    </ul>
                  </div>
                  <div className="liftoff_img_wrapper">
                    <img
                      src="assets/img/639b830a4cbdd20fd5f3a9a9/63f7ae17f6fe9766bf954ce8_image%2019%402x.png"
                      alt="Augusta Wellness Mobile App"
                      className="liftoff_png_img"
                    />
                  </div>
                </div>
              </div>

              <div className="col-lg-6">
                <div className="liftoff_card outstaffer_card">
                  <div>
                    <h3 className="heading-4">Outstaffer</h3>
                    <p className="sub_para">
                      Hire and build the best team on the planet
                    </p>
                    <ul className="d-flex list-unstyled boxes">
                      <li className="liftoff_tag">Software</li>
                      <li className="liftoff_tag">EOR</li>
                      <li className="liftoff_tag">Web</li>
                      <li className="liftoff_tag">Design</li>
                      <li className="liftoff_tag">+3</li>
                    </ul>
                  </div>
                  <div className="liftoff_img_wrapper">
                    <img
                      src="assets/img/639b830a4cbdd20fd5f3a9a9/639b830a4cbdd24eebf3aa81_outstaffer-thumb.png"
                      alt="Outstaffer Dashboard"
                      className="liftoff_png_img"
                    />
                  </div>
                </div>
              </div>
              <div className="col-3 d-md-none d-flex justify-content-center w-100 mt-md-0 mt-4">
                <button
                  type="button"
                  className="btn liftoff_btn w-100 py-2"
                >
                  View all
                </button>
              </div>
            </div>
          </div>
        </section> */}
        <Footer />
        <ContactModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
        />
      </ScrollToTop>
    </>
  );
}
